/**
 * REL-29 — every athlete-facing string on the deterministic age step, in ONE
 * plain file.
 *
 * Same posture as `onboardingSaveCopy.ts` (B26) and `oauthChipCopy.ts`: no
 * React, no router, no CSS in the graph, so the strings are readable and
 * pinnable on their own and a founder ratifying copy finds every word in one
 * place.
 *
 * ⚠️ EVERY STRING BELOW IS PROVISIONAL AND NEEDS FOUNDER RATIFICATION.
 *
 * Rules applied:
 *  - dash-free (the founder's no-em-dash rule for newly authored copy), pinned
 *    by test.
 *  - no exclamation marks, no "safe", no health categories, no all-clear claims
 *    (the minors-surface rules).
 *  - maximum plainness: this is the store-facing first touch, and a minor or a
 *    parent may be the one reading it.
 *
 * ⛔ B196 (founder ruling 2026-07-25, built as a scope-freeze exception,
 * spec v2 2026-09-17) — UNDER 13 IS A STOP, NOT A HOLD. A stated age under 13
 * at this screen ends the signup: nothing about the athlete is kept, on the
 * device or on the server, and the wizard never advances past this screen.
 * The only controls are the way back ("I picked the wrong year"), sign-out,
 * and removing the sign-in outright (no guardian, no cooling-off — there is
 * nothing to cool off and no account to keep). The server carries the same
 * floor on every owner context write (`handlePostContext`,
 * `age_below_minimum`); this screen is the front door, not the only one.
 *
 * The strings in `UNDER_13_COPY` are the spec's RATIFIED strings (v2 §4.8,
 * founder ratification item 7) — do not vary them without a new ratification.
 * The HOLD machinery this file used to describe (`guardian_consent_required`,
 * `Under13ConsentRow`) still exists for accounts that reached that state
 * before this ruling; it is untouched by this lane and never applies to a
 * NEW under-13 signup, which never gets an account at all.
 */

export const AGE_STEP_COPY = {
  /** OnboardingShell chrome for the step. */
  stepLabel: 'First',
  shellLabel: 'How old are you?',
  shellContext: 'Age sets your training limits, so we ask before anything else.',

  heading: 'When were you born?',
  sub: 'Month and year only. We never ask for the day.',

  monthLabel: 'Birth month',
  yearLabel: 'Birth year',
  monthPlaceholder: 'Month',
  yearPlaceholder: 'Year',

  cta: 'Continue',
  /** Shown under the CTA until both selects are answered. */
  incomplete: 'Pick a month and a year to continue.',

  /** 13 to 15: the guardian beat later in onboarding does the real work here. */
  minorNote: 'You can add a parent or guardian later. Training works the same either way.',
} as const;

export const UNDER_13_COPY = {
  heading: 'Tayro is for athletes 13 and up',
  /** Spec v2 §4.8, the `age_below_minimum` new-signup row, ratified. */
  body:
    'We did not keep anything you entered. You can remove this sign-in, or go back if you picked the wrong year.',
  /** The way back, in case the birth year was a mis-tap. */
  back: 'I picked the wrong year',
  /** Removes the sign-in outright — the F4 self-serve path for a refused
   *  signup: no established 13+ band, no guardian, no data to keep. The
   *  screen's third control, sign-out, is `OnboardingShell`'s own existing
   *  "Sign out" link (present on every onboarding screen) — not a string
   *  owned by this file. */
  remove: 'Remove this sign-in',
} as const;

/** The lower bound of `Age` this step (and the server floor) treats as under 13. */
export const UNDER_13_MAX_AGE = 12;

/**
 * Age from birth month + year, matching `deriveAge` in usePersistOnboarding and
 * `deriveAgeFromBirth` in api/lib/planning/clinicalModifiers.js EXACTLY:
 * during the birth month the birthday counts as NOT yet passed, so the number
 * errs one LOWER. Conservative in the direction that never over-permits a minor.
 */
export function ageFromBirth(birthYear: number, birthMonth: number, now: Date = new Date()): number {
  let age = now.getFullYear() - birthYear;
  if (now.getMonth() + 1 <= birthMonth) age -= 1;
  return age;
}

export function isUnder13(birthYear: number, birthMonth: number, now: Date = new Date()): boolean {
  return ageFromBirth(birthYear, birthMonth, now) <= UNDER_13_MAX_AGE;
}
